<template>
    <vx-expandable-card data-test="org-unit-billing-email">
        <template v-slot:header> Billing Email </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4 mt-3">
                <div>
                    <vx-input
                        :disabled="!props.isEditable"
                        size="lg"
                        placeholder="Email for billing"
                        v-model="currentBillingEmail"
                        :error="displayFirstError(validate.email.$errors)"
                    ></vx-input>
                </div>
                <div v-if="props.isEditable">
                    <vx-button
                        type="button"
                        size="lg"
                        :loading="saving"
                        :disabled="!props.isEditable"
                        @click="saveBillingEmail"
                        class="w-full"
                        data-test="update-email-button"
                    >
                        Update
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxButton, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { ref, nextTick } from 'vue';
import { orgUnitBillingClient } from '~/services';
import { displayFirstError } from '~/utils/validation';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
    billingEmail: {
        type: String,
        default: null,
    },
    isEditable: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const currentBillingEmail = ref(props.billingEmail);
const saving = ref(false);

const validate = useVuelidate(
    {
        email: { required, email },
    },
    {
        email: currentBillingEmail,
    }
);

const saveBillingEmail = async () => {
    if (saving.value) {
        return;
    }

    // Validate the email field
    validate.value.$touch();
    await nextTick();

    if (validate.value.$invalid) {
        return;
    }
    saving.value = true;

    try {
        await orgUnitBillingClient.updateBillingEmail(props.teamId, props.orgUnitId, {
            email: currentBillingEmail.value,
        });
        toasted.success('Billing email has been successfully updated');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        saving.value = false;
    }
};
</script>
